import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import {
  Plus, Minus, Send, LayoutGrid,
  UserCheck, X, RefreshCw, AlertCircle, Search,
} from 'lucide-react';
import { posRestaurant, posOrders } from '../../api/posAPI';
import usePosStore from '../../stores/posStore';
import { useWaiters } from './hooks/useWaiters';
import { useProducts } from './hooks/useProducts';
import TableSelector from './TableSelector';

// ─── Skeleton ─────────────────────────────────────────────────────────────────
function Skeleton({ className = '' }) {
  return <div className={`animate-pulse bg-gray-200 rounded ${className}`} />;
}

// ─── Inline Error ─────────────────────────────────────────────────────────────
function InlineError({ message, onRetry }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-3 text-red-600 p-4">
      <AlertCircle className="h-8 w-8" />
      <p className="text-sm text-center">{message}</p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          <RefreshCw className="h-3 w-3" /> Retry
        </button>
      )}
    </div>
  );
}

// ─── Waiter PIN Modal ─────────────────────────────────────────────────────────
function WaiterPinModal({ waiters, onVerified, onClose }) {
  const { posToken, setWaiter } = usePosStore();
  const [selected, setSelected] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleVerify = async () => {
    setError(null);
    setLoading(true);
    try {
      // Use the restaurant-scoped verify-pin endpoint
      const res = await posRestaurant.verifyWaiterPin(posToken, { staffId: selected, pin });
      const { staffId, name } = res.data;
      setWaiter(staffId, name); // stored in posStore.activeOrder only
      onVerified({ staffId, name });
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid PIN');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xs p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-gray-900 flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-blue-600" />
            Waiter Verify
          </h2>
          <button onClick={onClose}><X className="h-4 w-4 text-gray-400" /></button>
        </div>

        {error && (
          <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="mb-3">
          <label className="block text-xs font-semibold text-gray-700 mb-1">Select Waiter</label>
          {waiters.length === 0 ? (
            <p className="text-xs text-gray-400 italic">No waiters available</p>
          ) : (
            <select
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
            >
              <option value="">— select —</option>
              {waiters.map((w) => (
                <option key={w._id} value={w._id}>{w.name}</option>
              ))}
            </select>
          )}
        </div>

        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">PIN</label>
          <input
            type="password"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            maxLength={8}
            autoFocus
          />
        </div>

        <button
          onClick={handleVerify}
          disabled={!selected || !pin || loading}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 rounded-lg text-sm disabled:opacity-50"
        >
          {loading ? 'Verifying…' : 'Verify'}
        </button>
      </div>
    </div>
  );
}

// ─── Product Grid ─────────────────────────────────────────────────────────────
function ProductGrid({ groupedProducts, isLoading, error, onSelect, onRetry, cartItems = [] }) {
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchRaw, setSearchRaw] = useState('');
  const [search, setSearch] = useState('');

  // Debounce search 200ms
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchRaw.trim()), 200);
    return () => clearTimeout(t);
  }, [searchRaw]);

  const allProducts = useMemo(
    () => groupedProducts.flatMap(g => g.items),
    [groupedProducts]
  );

  const cartQtyMap = useMemo(() => {
    const m = {};
    for (const item of cartItems) m[item.menuItemId] = item.quantity;
    return m;
  }, [cartItems]);

  const displayItems = useMemo(() => {
    if (search) {
      return allProducts.filter(p =>
        p.name.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (activeCategory === 'All') return allProducts;
    return groupedProducts.find(g => g.category === activeCategory)?.items || [];
  }, [search, activeCategory, allProducts, groupedProducts]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-3 p-4">
        {Array.from({ length: 9 }).map((_, i) => (
          <Skeleton key={i} className="h-16" />
        ))}
      </div>
    );
  }
  if (error) return <InlineError message={error} onRetry={onRetry} />;
  if (groupedProducts.length === 0) {
    return <p className="text-sm text-gray-400 text-center p-8">No menu items found. Add products in the admin panel.</p>;
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Search bar */}
      <div className="px-3 pt-2 pb-1 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <input
            className="w-full pl-8 pr-8 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-gray-50"
            placeholder="Search menu items…"
            value={searchRaw}
            onChange={e => setSearchRaw(e.target.value)}
          />
          {searchRaw && (
            <button
              onClick={() => { setSearchRaw(''); setSearch(''); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Category tabs — hidden when searching */}
      {!search && (
        <div className="flex gap-1 px-3 py-1.5 border-b border-gray-200 overflow-x-auto flex-shrink-0">
          <button
            onClick={() => setActiveCategory('All')}
            className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
              activeCategory === 'All' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({allProducts.length})
          </button>
          {groupedProducts.map(g => (
            <button
              key={g.category}
              onClick={() => setActiveCategory(g.category)}
              className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${
                activeCategory === g.category ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {g.category} ({g.items.length})
            </button>
          ))}
        </div>
      )}

      {/* Items */}
      <div className="flex-1 overflow-y-auto">
        {displayItems.length === 0 ? (
          <p className="text-sm text-gray-400 text-center p-8">
            {search ? `No items match "${search}"` : 'No items in this category'}
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-3 p-3">
            {displayItems.map(p => {
              const qtyInCart = cartQtyMap[p._id] || 0;
              return (
                <button
                  key={p._id}
                  onClick={() => onSelect(p)}
                  className="relative p-3 rounded-xl border border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-left transition-colors"
                >
                  {qtyInCart > 0 && (
                    <span className="absolute top-1.5 right-1.5 bg-blue-600 text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                      {qtyInCart}
                    </span>
                  )}
                  <p className="text-sm font-medium text-gray-900 leading-tight pr-4">{p.name}</p>
                  <p className="text-xs text-blue-600 font-semibold mt-0.5">₱{(p.price / 100).toFixed(2)}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5 truncate">{p.category}</p>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Customer Search ──────────────────────────────────────────────────────────
function CustomerSearch({ posToken, value, onSelect }) {
  const [searchTerm, setSearchTerm]         = useState('');
  const [customers, setCustomers]           = useState([]);
  const [loading, setLoading]               = useState(false);
  const [showDropdown, setShowDropdown]     = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(value || null);

  // Sync from parent (e.g. when order type changes and parent clears)
  useEffect(() => {
    if (value) {
      setSelectedCustomer(value);
      setSearchTerm(value.customerName || '');
    } else {
      setSelectedCustomer(null);
      setSearchTerm('');
    }
  }, [value]);

  const doSearch = useCallback(
    debounce(async (term) => {
      if (!term || term.trim().length < 2) { setCustomers([]); return; }
      setLoading(true);
      try {
        const res = await posOrders.customerSearch(posToken, term);
        setCustomers(res.data.customers || []);
      } catch {
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    }, 250),
    [posToken]
  );

  const handleInputChange = (e) => {
    const term = e.target.value;
    setSearchTerm(term);
    setShowDropdown(true);
    if (selectedCustomer) {
      setSelectedCustomer(null);
      onSelect(null);
    }
    doSearch(term);
  };

  const handleSelect = (customer) => {
    setSelectedCustomer(customer);
    setSearchTerm(customer.customerName || '');
    setShowDropdown(false);
    setCustomers([]);
    onSelect(customer);
  };

  const handleClear = () => {
    setSelectedCustomer(null);
    setSearchTerm('');
    setCustomers([]);
    setShowDropdown(false);
    onSelect(null);
  };

  // Click outside
  useEffect(() => {
    const handle = (e) => {
      if (!e.target.closest('.pos-customer-search')) setShowDropdown(false);
    };
    document.addEventListener('click', handle);
    return () => document.removeEventListener('click', handle);
  }, []);

  const showNoResults = showDropdown && searchTerm.trim().length >= 2 && customers.length === 0 && !loading;

  return (
    <div className="pos-customer-search relative">
      <div className="relative">
        <input
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search customer by name or card…"
          autoComplete="off"
          className="w-56 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400"
        />
        {selectedCustomer && !loading && (
          <button
            type="button"
            onClick={handleClear}
            title="Clear"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
        {loading && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <svg className="animate-spin h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
        )}
      </div>

      {/* Dropdown results */}
      {showDropdown && customers.length > 0 && (
        <div className="absolute z-50 w-72 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {customers.map((c, i) => (
            <div
              key={i}
              onClick={() => handleSelect(c)}
              onMouseDown={(e) => e.preventDefault()}
              className="px-3 py-2 border-b border-gray-100 last:border-0 hover:bg-indigo-50 cursor-pointer"
            >
              <div className="font-medium text-sm text-gray-900">{c.customerName}</div>
              <div className="text-xs text-gray-500 flex gap-3">
                {c.customerNo && <span>Card: {c.customerNo}</span>}
                <span>{c.orderCount} order{c.orderCount !== 1 ? 's' : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No results */}
      {showNoResults && (
        <div className="absolute z-50 w-72 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3 space-y-2">
          <p className="text-sm text-gray-500 text-center">No customers found for "{searchTerm}"</p>
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => handleSelect({ customerName: searchTerm.trim(), customerNo: '' })}
            className="w-full text-sm text-indigo-600 font-medium hover:underline"
          >
            + Use "{searchTerm.trim()}" as new customer
          </button>
        </div>
      )}

      {/* Selected customer card */}
      {selectedCustomer && (
        <div className="mt-1 px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-lg flex items-start justify-between gap-2">
          <div>
            <p className="text-sm font-medium text-indigo-900">{selectedCustomer.customerName}</p>
            {selectedCustomer.customerNo && (
              <p className="text-xs text-indigo-600">Card: {selectedCustomer.customerNo}</p>
            )}
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="text-xs text-indigo-500 hover:text-indigo-700 shrink-0"
          >
            Change
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Main OrderScreen ──────────────────────────────────────────────────────────
export default function OrderScreen() {
  const {
    posToken, activeOrder, addItem, updateItemQty, clearOrder,
  } = usePosStore();
  const { pax = 1 } = activeOrder;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [showWaiterPin, setShowWaiterPin] = useState(false);
  const [orderType, setOrderType] = useState('dine-in');
  const [customer, setCustomer] = useState(null); // { customerName, customerNo }
  const [sendError, setSendError] = useState(null);

  const isAddingToOrder = !!activeOrder.orderId;

  const { waiters, isLoading: waitersLoading } = useWaiters();
  const { groupedProducts, isLoading: productsLoading, error: productsError, refetch: refetchProducts } = useProducts();

  const sendMutation = useMutation({
    mutationFn: async () => {
      if (!activeOrder.waiterId) throw new Error('Verify a waiter first');
      if (!activeOrder.items.length) throw new Error('Add at least one item');

      const items = activeOrder.items.map((i) => ({
        menuItemId: i.menuItemId,
        name: i.name,
        quantity: i.quantity,
        notes: i.notes || '',
        printStation: i.printStation || 'kitchen',
      }));

      if (activeOrder.orderId) {
        return posOrders.addItems(posToken, activeOrder.orderId, { items });
      }

      if (orderType === 'dine-in' && !activeOrder.tableId) throw new Error('Select a table first');
      return posOrders.create(posToken, {
        type: orderType,
        tableId: orderType === 'dine-in' ? activeOrder.tableId : undefined,
        waiterId: activeOrder.waiterId,
        waiterName: activeOrder.waiterName,
        pax: activeOrder.pax || 1,
        customerName: customer?.customerName?.trim() || null,
        customerNo:   customer?.customerNo?.trim()   || null,
        items,
      });
    },
    onSuccess: () => {
      clearOrder();
      queryClient.invalidateQueries({ queryKey: ['pos', 'tables'] });
      queryClient.invalidateQueries({ queryKey: ['pos', 'active-orders'] });
      navigate('/pos/floor');
    },
    onError: (err) => {
      setSendError(err.response?.data?.message || err.message || 'Failed to send order');
    },
  });

  const handleProductSelect = (product) => {
    addItem({
      menuItemId: product._id,
      name: product.name,
      price: product.price, // centavos
      printStation: product.printStation || 'kitchen',
    });
  };

  const handleSend = () => {
    setSendError(null);
    if (!activeOrder.waiterId) {
      setShowWaiterPin(true);
      return;
    }
    sendMutation.mutate();
  };

  const cartTotal = activeOrder.items.reduce((s, i) => s + i.price * i.quantity, 0);
  const cartCount = activeOrder.items.reduce((s, i) => s + i.quantity, 0);

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center gap-3 flex-shrink-0 flex-wrap">
        {isAddingToOrder ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-700 font-medium">
            <Plus className="h-4 w-4" />
            Adding to {activeOrder.tableName} · {activeOrder.waiterName}
          </div>
        ) : (
          <>
            {/* Order type selector */}
            <select
              value={orderType}
              onChange={(e) => { setOrderType(e.target.value); setCustomer(null); }}
              className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            >
              <option value="dine-in">Dine-In</option>
              <option value="takeout">Takeout</option>
            </select>

            {/* Table selector — hidden for takeout */}
            {orderType === 'dine-in' && <TableSelector />}

            {/* Customer search — takeout only */}
            {orderType === 'takeout' && (
              <CustomerSearch
                posToken={posToken}
                value={customer}
                onSelect={setCustomer}
              />
            )}
          </>
        )}

        <div className="flex-1" />

        {/* Waiter verify — hidden when adding to existing order (waiter is locked) */}
        {!isAddingToOrder && (
          activeOrder.waiterName ? (
            <span className="text-xs text-green-700 bg-green-50 px-2 py-1 rounded-full flex items-center gap-1">
              <UserCheck className="h-3 w-3" />
              {activeOrder.waiterName}
              <button
                onClick={() => setShowWaiterPin(true)}
                className="ml-1 text-green-600 hover:text-green-800 underline text-[11px]"
              >
                change
              </button>
            </span>
          ) : (
            <button
              onClick={() => setShowWaiterPin(true)}
              disabled={waitersLoading}
              className="text-sm text-blue-600 border border-blue-300 hover:bg-blue-50 px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              {waitersLoading ? 'Loading…' : '+ Verify Waiter'}
            </button>
          )
        )}
      </div>

      {/* Banners */}
      {sendError && (
        <div className="mx-4 mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700 flex items-center justify-between flex-shrink-0">
          <span>{sendError}</span>
          <button className="ml-2 underline text-xs" onClick={() => setSendError(null)}>dismiss</button>
        </div>
      )}

      {/* Body */}
      <div className="flex-1 overflow-hidden flex">
        {/* Left panel — Product grid (always visible) */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50 flex items-center gap-2 flex-shrink-0">
            <LayoutGrid className="h-4 w-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700">
              Menu
              {activeOrder.tableName && (
                <span className="font-normal text-gray-500 ml-1">— {activeOrder.tableName}</span>
              )}
            </h2>
          </div>
          <div className="flex-1 overflow-hidden">
            <ProductGrid
              groupedProducts={groupedProducts}
              isLoading={productsLoading}
              error={productsError}
              onSelect={handleProductSelect}
              onRetry={refetchProducts}
              cartItems={activeOrder.items}
            />
          </div>
        </div>

        {/* Right panel — Cart */}
        <div className="w-72 border-l border-gray-200 bg-white flex flex-col flex-shrink-0">
          <div className="px-4 py-3 border-b border-gray-200 flex-shrink-0">
            <h2 className="font-semibold text-gray-900 text-sm">
              Cart {cartCount > 0 ? `(${cartCount})` : ''}
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
            {activeOrder.items.length === 0 ? (
              <p className="p-4 text-sm text-gray-400 text-center">
                Tap menu items to add
              </p>
            ) : (
              activeOrder.items.map((item) => (
                <div key={item.menuItemId} className="px-4 py-3 flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 leading-tight truncate">{item.name}</p>
                    <p className="text-xs text-gray-500">₱{(item.price / 100).toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => updateItemQty(item.menuItemId, item.quantity - 1)}
                      className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="w-6 text-center text-sm font-medium">{item.quantity}</span>
                    <button
                      onClick={() => updateItemQty(item.menuItemId, item.quantity + 1)}
                      className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-4 border-t border-gray-200 space-y-3 flex-shrink-0">
            {cartCount > 0 && (
              <div className="flex justify-between text-sm font-semibold text-gray-900">
                <span>Subtotal</span>
                <span>₱{(cartTotal / 100).toFixed(2)}</span>
              </div>
            )}
            <button
              onClick={handleSend}
              disabled={activeOrder.items.length === 0 || sendMutation.isPending}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 text-sm transition-colors"
            >
              <Send className="h-4 w-4" />
              {sendMutation.isPending
                ? (isAddingToOrder ? 'Adding…' : 'Sending…')
                : (isAddingToOrder ? 'Add to Order' : 'Send to Kitchen')}
            </button>
          </div>
        </div>
      </div>

      {showWaiterPin && (
        <WaiterPinModal
          waiters={waiters}
          onVerified={() => {
            setShowWaiterPin(false);
            // If items exist, send immediately after waiter verify
            if (activeOrder.items.length > 0) sendMutation.mutate();
          }}
          onClose={() => setShowWaiterPin(false)}
        />
      )}
    </div>
  );
}
